import { Country } from '@consigli/types';
import { type TFunction } from 'i18next';

export interface FieldOption {
  label: string;
  value: string;
}

/** The `country.*` key for a country, e.g. `HONG_KONG` -> `country.hong-kong`. */
export const countryLabelKey = (country: Country): string =>
  `country.${country.toLowerCase().replace(/_/g, '-')}`;

/**
 * Every country the type permits.
 *
 * `Object.values(Country)` rather than a hand-kept list: the enum is already the
 * curated offer — the ones the business has not enabled are commented out in
 * `project-properties.ts` — so a second list here could only drift from it. The
 * exceptions are countries an existing project may hold but a new one may not be
 * created in; `NOT_OFFERED_FOR_NEW_PROJECTS` below lists those.
 *
 * It matters that this is the *whole* enum. A select that offered less could not
 * render a project already set to the value it left out, and would silently show
 * an empty country for it. The create forms offer the narrower
 * `newProjectCountryOptions`, which must not decide what an *existing* project
 * can display.
 *
 * `OTHER` is in the list deliberately — selecting it turns off the
 * standards-related outputs in Risks and Insights, as the Settings helper text
 * spells out.
 *
 * Sorted by the *translated* label, so each locale reads alphabetically in its
 * own language.
 */
export const countryOptions = (t: TFunction): FieldOption[] =>
  Object.values(Country)
    .map((value) => ({ label: t(countryLabelKey(value)), value }))
    .sort((a, b) => a.label.localeCompare(b.label));

/** Countries the enum allows on an existing project but the create forms do not offer. */
const NOT_OFFERED_FOR_NEW_PROJECTS: ReadonlySet<string> = new Set([Country.UNITED_ARAB_EMIRATES]);

/**
 * The countries a new project can be created in: the list the legacy form in
 * features/project-creation/fields/project-country-field.tsx offers.
 */
export const newProjectCountryOptions = (t: TFunction): FieldOption[] =>
  countryOptions(t).filter((option) => !NOT_OFFERED_FOR_NEW_PROJECTS.has(option.value));
